import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Brain, ShieldAlert, Gauge, ScanSearch, CheckCircle2 } from 'lucide-react';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { DASHBOARD_SIGNIN_URL } from '@/config/links';
import ProductFAQAccordion from '@/components/products/ProductFAQAccordion';

const rtoFaqs = [
  { question: 'What is an RTO score?', answer: 'An RTO score is a predictive value that estimates the likelihood of a shipment being returned to origin.' },
  { question: 'Can this detect high-risk COD orders?', answer: 'Yes, RTO Predict flags risky COD orders early and helps trigger verification workflows.' },
  { question: 'How does address validation work?', answer: 'The model analyzes address quality and historical delivery patterns to identify risky entries.' },
  { question: 'Does it help with fraud prevention?', answer: 'Yes, fraud-like order behavior is captured through risk signals and surfaced before dispatch.' },
  { question: 'Who should use RTO Predict?', answer: 'Any eCommerce team with COD exposure and high failed-delivery costs should use it.' }
];

const getRiskTier = (score) => {
  if (score < 34) {
    return {
      label: 'Low',
      text: 'text-green-700',
      bar: 'bg-gradient-to-r from-green-400 to-green-600'
    };
  }
  if (score < 67) {
    return {
      label: 'Medium',
      text: 'text-amber-700',
      bar: 'bg-gradient-to-r from-amber-400 to-amber-500'
    };
  }
  return {
    label: 'High',
    text: 'text-red-700',
    bar: 'bg-gradient-to-r from-red-500 to-red-700'
  };
};

const ProductRTOPredict = () => {
  const riskScore = 27;
  const risk = getRiskTier(riskScore);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="RTO Predict: AI Risk Scoring for COD and Failed Delivery Prevention"
        description="Predict high-risk orders with AI, validate addresses, detect fraud patterns, and reduce return-to-origin losses."
        keywords="rto prediction tool, cod risk scoring, failed delivery prevention, ecommerce fraud detection ai"
        canonical="https://boxdlogistics.in/products/rto-predict"
      />
      <Navbar />

      <section className="relative overflow-hidden bg-gradient-to-b from-blue-100/50 via-blue-50/60 to-white px-4 pb-20 pt-28 md:pt-32">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(59, 130, 246,0.22),transparent_35%)]" />
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="inline-flex rounded-full border border-blue-200 bg-white px-4 py-1 text-sm font-semibold text-blue-900">
              AI Risk Intelligence
            </span>
            <h1 className="mt-4 text-4xl font-bold leading-tight text-gray-900 md:text-6xl">
              RTO Predict Helps You Stop High-Risk COD Orders Before Dispatch
            </h1>
            <p className="mt-5 max-w-xl text-lg text-gray-600">
              Score every order with AI, validate risky addresses, and trigger pre-dispatch checks that reduce failed deliveries and shipment loss.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button className="bg-gradient-to-r from-blue-800 to-blue-900 text-white hover:from-blue-900 hover:to-blue-950" onClick={() => { window.location.href = DASHBOARD_SIGNIN_URL; }}>
                Enable RTO Predict <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button variant="outline" className="border-blue-300 text-blue-900 hover:bg-blue-50" asChild>
                <Link to="/contact">Book Demo</Link>
              </Button>
            </div>
          </div>

          <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-gray-900">Live RTO Score Visualization</h2>
            <div className="mt-4 rounded-2xl bg-gray-50 p-5">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm text-gray-600">Order Risk Score</p>
                <span className={`text-sm font-semibold ${risk.text}`}>{riskScore} / 100 ({risk.label})</span>
              </div>
              <div className="relative h-3 overflow-hidden rounded-full bg-gray-200">
                <div className={`h-3 rounded-full ${risk.bar}`} style={{ width: `${riskScore}%` }} />
              </div>
              <div className="mt-2 flex justify-between text-[10px] font-medium uppercase tracking-wide text-gray-400">
                <span>Low</span>
                <span>Medium</span>
                <span>High</span>
              </div>
              <div className="mt-5 grid grid-cols-3 gap-3 text-center">
                <div className="rounded-lg bg-white p-3">
                  <p className="text-xs text-gray-500">Address Trust</p>
                  <p className="text-lg font-bold text-green-700">92%</p>
                </div>
                <div className="rounded-lg bg-white p-3">
                  <p className="text-xs text-gray-500">COD Risk</p>
                  <p className="text-lg font-bold text-green-700">Low</p>
                </div>
                <div className="rounded-lg bg-white p-3">
                  <p className="text-xs text-gray-500">Fraud Signal</p>
                  <p className="text-lg font-bold text-green-700">Clear</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-16">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-center text-3xl font-bold text-gray-900 md:text-4xl">Problem to Solution: Fixing RTO at the Root</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6">
              <h3 className="text-xl font-semibold text-gray-800">The Problem</h3>
              <ul className="mt-4 space-y-2 text-sm text-gray-700">
                <li>High-risk COD orders shipped without pre-checks</li>
                <li>Invalid addresses causing repeated failed attempts</li>
                <li>Undetected fraud patterns increasing losses</li>
              </ul>
            </div>
            <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-6">
              <h3 className="text-xl font-semibold text-blue-900">The RTO Predict Solution</h3>
              <ul className="mt-4 space-y-2 text-sm text-blue-950">
                <li>AI score before dispatch for every order</li>
                <li>Address intelligence with verification triggers</li>
                <li>Fraud signals mapped to dispatch decisions</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-50 px-4 py-16">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-3xl font-bold text-gray-900 md:text-4xl">How AI Prediction Works</h2>
          <div className="mt-8 grid gap-5 lg:grid-cols-5">
            {[
              { title: 'Data Intake', desc: 'Collect order, buyer, and address attributes', icon: ScanSearch },
              { title: 'AI Scoring', desc: 'Evaluate historical patterns and risk behavior', icon: Brain },
              { title: 'Validation', desc: 'Run checks on suspicious inputs and COD signals', icon: ShieldAlert },
              { title: 'Decision Layer', desc: 'Approve, verify, or hold based on score threshold', icon: Gauge },
              { title: 'Outcome Learning', desc: 'Improve model quality using delivery outcomes', icon: CheckCircle2 }
            ].map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={step.title} className="rounded-2xl border border-gray-200 bg-white p-5">
                  <p className="text-xs uppercase tracking-wide text-blue-900">Phase {index + 1}</p>
                  <Icon className="mt-3 h-5 w-5 text-blue-900" />
                  <h3 className="mt-3 text-base font-semibold text-gray-900">{step.title}</h3>
                  <p className="mt-2 text-sm text-gray-600">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-16">
        <div className="mx-auto grid max-w-6xl gap-5 md:grid-cols-3">
          {[
            { label: 'Failed deliveries reduced', value: '38%' },
            { label: 'Risky COD orders intercepted', value: '2.7x' },
            { label: 'Fraud-related losses reduced', value: '31%' }
          ].map((metric) => (
            <div key={metric.label} className="rounded-2xl border border-blue-100 bg-blue-50/60 p-6 text-center">
              <p className="text-4xl font-bold text-blue-900">{metric.value}</p>
              <p className="mt-2 text-sm text-gray-700">{metric.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-gradient-to-r from-blue-800 to-blue-900 px-4 py-16 text-white">
        <div className="mx-auto max-w-5xl text-center">
          <h2 className="text-3xl font-bold md:text-4xl">Reduce RTO & Increase Conversions</h2>
          <p className="mx-auto mt-3 max-w-2xl text-blue-100">
            Add AI-powered risk intelligence to your fulfillment flow and stop preventable delivery failures before they happen.
          </p>
          <Button className="mt-7 bg-white text-blue-900 hover:bg-blue-50" onClick={() => { window.location.href = DASHBOARD_SIGNIN_URL; }}>
            Start with RTO Predict <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <ProductFAQAccordion title="RTO Predict FAQs" items={rtoFaqs} className="pt-16" />

      <Footer />
    </div>
  );
};

export default ProductRTOPredict;
